/**
 * The voice list, fetched once for the whole settings section.
 *
 * Eight announcements are edited across three tabs and every one of them offers
 * the same voices, so the request is shared rather than repeated per control.
 * The cache is a promise, not a result: two pickers mounting in the same frame
 * both await the one request instead of racing two.
 *
 * Module-level and never invalidated on a timer, which is the correct lifetime
 * for what this is - opening the settings section is the whole session, and a
 * voice added elsewhere meanwhile is not something anyone is waiting on. A
 * reload refetches, because the module goes with the page.
 *
 * ⚠ IT IS INVALIDATED BY ONE THING: SAVING A DIFFERENT PROVIDER. The list is a
 * property of whichever service is switched on, and the endpoint answers for
 * whatever the stored setting says. Without `forgetTtsVoices` on that save, an
 * operator who moves to ElevenLabs on the Transcription tab and walks over to
 * the announcements keeps being offered TartuNLP speakers for the rest of the
 * session, and every one of them is a value the new provider will refuse.
 *
 * Failure is not thrown at the caller. The API answers with a fallback list when
 * TartuNLP is unreachable and with an empty list plus a reason when ElevenLabs
 * has no key, so the only way to get here empty-and-failed is the API itself
 * being down - at which point every other panel on the page is already saying so.
 */
import { useEffect, useState } from "react";
import { listTtsVoices, type TtsVoiceListing } from "../../../../lib/calls";

let cached: Promise<TtsVoiceListing> | null = null;
/** Bumped by `forgetTtsVoices` so mounted pickers refetch rather than sit stale. */
let generation = 0;

function load(): Promise<TtsVoiceListing> {
  // A failed attempt clears itself, so the next picker to mount tries again
  // rather than inheriting an empty list for the rest of the session.
  cached ??= listTtsVoices().catch((err) => {
    cached = null;
    throw err;
  });
  return cached;
}

/**
 * Drop the cached list. Called after a save that may have changed the provider.
 *
 * Only THIS tab is reached - other open CRM tabs pick the new provider up when
 * they next reload, the same compromise `forgetCallWindowPrefs` makes for the
 * call window defaults.
 */
export function forgetTtsVoices(): void {
  cached = null;
  generation += 1;
}

export interface TtsVoicesState {
  listing: TtsVoiceListing | null;
  loading: boolean;
  /** The list could not be fetched at all - not the same as an empty one. */
  failed: boolean;
}

export function useTtsVoices(): TtsVoicesState {
  const [state, setState] = useState<TtsVoicesState>({ listing: null, loading: true, failed: false });
  const [seen, setSeen] = useState(generation);

  // A picker mounted before the provider changed is still on screen after the
  // save, so it re-reads the counter rather than waiting for a remount that is
  // not coming.
  if (seen !== generation) setSeen(generation);

  useEffect(() => {
    let alive = true;
    setState((prev) => ({ ...prev, loading: true }));
    load()
      .then((listing) => alive && setState({ listing, loading: false, failed: false }))
      .catch(() => alive && setState({ listing: null, loading: false, failed: true }));
    return () => {
      alive = false;
    };
  }, [seen]);

  return state;
}
